import { useEffect, useState, useCallback, useRef } from "react";
import { api, mediaUrl, uploadFile } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSettings } from "@/context/SettingsContext";
import { ConfirmButton } from "@/components/ConfirmButton";
import { Shield, Megaphone, Palette, Users, UserCog, KeyRound, Check, X, Trash2, ImagePlus, Plus, Loader2, ExternalLink, Inbox, Database, Upload, Download, List, Eye, MousePointerClick, Search, CreditCard } from "lucide-react";
import { toast } from "sonner";

const TABS = [
  { key: "overview", label: "Overview", icon: Shield },
  { key: "moderation", label: "Moderation", icon: Inbox },
  { key: "catalog", label: "Catalog Import", icon: Database },
  { key: "ads", label: "Create Ad", icon: Megaphone },
  { key: "ads_list", label: "All Ads", icon: List },
  { key: "theme", label: "Theme & Ads Control", icon: Palette },
  { key: "admins", label: "Administrators", icon: Users },
  { key: "members", label: "Members", icon: UserCog },
];

const THEME_MODES = [
  { key: "light_parchment", label: "Vintage Parchment" },
  { key: "dark_obsidian_amber", label: "Obsidian Amber" },
  { key: "antique_aqua_vault", label: "Antique Aqua" },
  { key: "cobalt_vault", label: "Deep Cobalt" },
];

const DISPLAY_FONTS = ["Cormorant Garamond", "Playfair Display", "DM Serif Display", "Fraunces", "Space Grotesk", "Bebas Neue", "Abril Fatface", "Marcellus"];
const BODY_FONTS = ["Manrope", "Inter", "Work Sans", "Nunito Sans", "Source Sans 3", "Rubik", "Karla", "Lato"];

function useUpload() {
  const [uploading, setUploading] = useState(false);
  const upload = async (file) => {
    setUploading(true);
    try {
      return await uploadFile(file);
    } catch (err) {
      toast.error(err.message || "Upload failed");
      return null;
    } finally {
      setUploading(false);
    }
  };
  return { upload, uploading };
}

export default function Admin() {
  const { user } = useAuth();
  const { loadSettings } = useSettings();
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [ads, setAds] = useState([]);
  const [adminSettings, setAdminSettings] = useState(null);
  const [admins, setAdmins] = useState([]);
  const [pendingIns, setPendingIns] = useState([]);
  const [contributions, setContributions] = useState([]);
  const isOwner = user?.role === "owner";

  const loadAll = useCallback(async () => {
    const [s, a, set, ad, pi, cb] = await Promise.all([
      api.get("/admin/stats"), api.get("/ads/all"), api.get("/admin/settings"), api.get("/admin/admins"),
      api.get("/admin/insulators/pending"), api.get("/admin/contributions"),
    ]);
    setStats(s.data); setAds(a.data); setAdminSettings(set.data); setAdmins(ad.data);
    setPendingIns(pi.data); setContributions(cb.data);
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  return (
    <div className="max-w-6xl mx-auto px-4 lg:px-8 py-10">
      <p className="font-tech text-xs uppercase tracking-widest brand-text">Control room</p>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <h1 className="font-display text-4xl font-bold">Admin Panel</h1>
        <a href="https://app.emergent.sh" target="_blank" rel="noopener noreferrer" data-testid="edit-in-emergent-link" className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold surface border hover:shadow">
          <ExternalLink className="w-4 h-4" /> Edit this app on Emergent
        </a>
      </div>

      <div className="flex flex-wrap gap-2 mb-8 border-b pb-3" style={{ borderColor: "var(--page-border)" }}>
        {TABS.map((t) => (
          <button key={t.key} data-testid={`admin-tab-${t.key}`} onClick={() => setTab(t.key)} className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold transition-colors ${tab === t.key ? "text-white" : "surface border"}`} style={tab === t.key ? { background: "var(--brand)" } : {}}>
            <t.icon className="w-4 h-4" /> {t.label}
          </button>
        ))}
      </div>

      {tab === "overview" && stats && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-7">
          {[
            ["Collectors", stats.users], ["Insulators", stats.insulators], ["Forum topics", stats.topics],
            ["Ads pending", stats.ads_pending], ["Ads live", stats.ads_active],
            ["Insulators pending", stats.insulators_pending], ["Additions pending", stats.contributions_pending],
          ].map(([label, val]) => (
            <div key={label} className="surface border rounded-2xl p-5">
              <p className="text-xs font-tech uppercase tracking-widest text-muted-page">{label}</p>
              <p className="font-display text-4xl font-bold mt-1">{val}</p>
            </div>
          ))}
        </div>
      )}

      {tab === "moderation" && <ModerationPanel pendingIns={pendingIns} contributions={contributions} reload={loadAll} />}
      {tab === "catalog" && <CatalogImportPanel reload={loadAll} total={stats?.insulators} />}
      {tab === "ads" && <AdsManager reload={loadAll} />}
      {tab === "ads_list" && <AdsListPanel ads={ads} reload={loadAll} />}
      {tab === "theme" && adminSettings && <ThemePanel settings={adminSettings} reload={async () => { await loadAll(); await loadSettings(); }} />}
      {tab === "admins" && <AdminsPanel admins={admins} isOwner={isOwner} reload={loadAll} />}
      {tab === "members" && <MembersPanel isOwner={isOwner} currentUserId={user?.id} />}
    </div>
  );
}

function CatalogImportPanel({ reload, total }) {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const HEADERS = "cd_number,name,maker,material,color,rarity,rarity_index,year,thread_type,base_type,country,est_value_low,est_value_high,description";

  const downloadTemplate = () => {
    const sample = HEADERS + "\nCD 145,Beehive,Hemingray,Glass,Aqua,Common,2,1893-1920,Threaded,Smooth Base,United States of America,8,25,The classic beehive insulator";
    const blob = new Blob([sample], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "insulator-catalog-template.csv";
    document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
  };

  const doImport = async () => {
    if (!file) return toast.error("Choose a CSV file first");
    setBusy(true); setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/insulators/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(data);
      toast.success(`Imported ${data.added} · skipped ${data.skipped}${data.errors ? ` · ${data.errors} errors` : ""}`);
      await reload();
    } catch (e) {
      toast.error("Import failed — check the CSV format");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-2xl space-y-6" data-testid="admin-catalog-panel">
      <div className="surface border rounded-2xl p-6">
        <div className="flex items-center gap-2 mb-1"><Database className="w-5 h-5 brand-text" /><h2 className="font-display text-2xl font-bold">Bulk-import insulators</h2></div>
        <p className="text-muted-page text-sm mb-4">Upload a CSV to add many insulators at once. Rows matching an existing CD number + name + color are skipped, so you can safely re-import. Current catalog: <span className="font-semibold brand-text">{total ?? "…"}</span> insulators.</p>

        <button data-testid="download-template-btn" onClick={downloadTemplate} className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold surface border mb-4"><Download className="w-4 h-4" /> Download CSV template</button>

        <label className="block text-xs font-tech uppercase tracking-widest text-muted-page mb-1">CSV file</label>
        <input data-testid="catalog-csv-input" type="file" accept=".csv,text/csv" onChange={(e) => setFile(e.target.files?.[0] || null)} className="w-full surface border rounded-lg px-3 py-2.5 mb-4" />

        <button data-testid="import-csv-btn" onClick={doImport} disabled={busy} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-semibold text-white disabled:opacity-60" style={{ background: "var(--brand)" }}>
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} {busy ? "Importing…" : "Import CSV"}
        </button>

        {result && (
          <div className="mt-4 text-sm surface border rounded-lg p-3" data-testid="import-result">
            <span className="text-emerald-700 font-semibold">{result.added} added</span> · <span className="text-muted-page">{result.skipped} skipped (duplicates)</span>{result.errors ? <> · <span className="text-rose-600">{result.errors} errors</span></> : null}
          </div>
        )}
      </div>
      <div className="surface border rounded-2xl p-6 text-sm text-muted-page">
        <p className="font-semibold mb-2" style={{ color: "var(--page-fg)" }}>Column reference</p>
        <p className="font-mono text-xs break-words">{HEADERS}</p>
        <p className="mt-3">Only <span className="font-semibold">cd_number</span> or <span className="font-semibold">name</span> is required per row; the rest are optional. <span className="font-semibold">material</span> accepts Glass or Porcelain. Country auto-fills from the maker when blank.</p>
      </div>
    </div>
  );
}


function AdsManager({ reload }) {
  const { upload, uploading } = useUpload();
  const [form, setForm] = useState({ title: "", sponsor_name: "", target_url: "", position: "home_top", size: "medium", image_url: "", active: true });

  const create = async () => {
    if (!form.title.trim()) return toast.error("Add a title");
    await api.post("/ads", form);
    toast.success("Ad created");
    setForm({ title: "", sponsor_name: "", target_url: "", position: "home_top", size: "medium", image_url: "", active: true });
    reload();
  };

  return (
    <div className="max-w-xl">
      <div className="surface border rounded-2xl p-6 space-y-4 h-fit" data-testid="admin-create-ad">
        <h2 className="font-display text-2xl font-bold">Create an ad</h2>
        <p className="text-sm text-muted-page">New ads are approved instantly and appear in the <strong>All Ads</strong> tab.</p>
        <input placeholder="Title" data-testid="admin-ad-title" value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} className="w-full surface border rounded-lg px-3 py-2.5" />
        <input placeholder="Sponsor name" value={form.sponsor_name} onChange={(e) => setForm((f) => ({ ...f, sponsor_name: e.target.value }))} className="w-full surface border rounded-lg px-3 py-2.5" />
        <input placeholder="Target URL" value={form.target_url} onChange={(e) => setForm((f) => ({ ...f, target_url: e.target.value }))} className="w-full surface border rounded-lg px-3 py-2.5" />
        <select data-testid="admin-ad-position" value={form.position} onChange={(e) => setForm((f) => ({ ...f, position: e.target.value }))} className="w-full surface border rounded-lg px-3 py-2.5">
          <option value="home_top">Homepage banner</option>
          <option value="home_side">Homepage side rail</option>
          <option value="catalog">Catalog page</option>
          <option value="login">Login page</option>
        </select>
        <div>
          <label className="block text-xs font-tech uppercase tracking-widest text-muted-page mb-1">Ad size</label>
          <div className="grid grid-cols-3 gap-2" data-testid="admin-ad-size">
            {["small", "medium", "large"].map((s) => (
              <button key={s} type="button" onClick={() => setForm((f) => ({ ...f, size: s }))} className={`px-3 py-2 rounded-lg text-sm font-semibold border capitalize ${form.size === s ? "text-white" : "surface"}`} style={form.size === s ? { background: "var(--brand)" } : {}}>{s}</button>
            ))}
          </div>
        </div>
        <label className="cursor-pointer inline-flex items-center gap-2 px-4 py-2 rounded-full surface border text-sm font-semibold">
          {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImagePlus className="w-4 h-4" />} Upload image
          <input type="file" accept="image/*" className="hidden" onChange={async (e) => { const f = e.target.files?.[0]; if (f) { const url = await upload(f); setForm((s) => ({ ...s, image_url: url })); } }} />
        </label>
        {form.image_url && <img src={mediaUrl(form.image_url)} alt="" className="w-full h-28 object-cover rounded-lg" />}
        <button data-testid="admin-create-ad-btn" onClick={create} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-semibold text-white" style={{ background: "var(--brand)" }}><Plus className="w-4 h-4" /> Create ad</button>
      </div>
    </div>
  );
}

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }); }
  catch { return "—"; }
};

const PAY_STYLES = {
  paid: "bg-emerald-100 text-emerald-700",
  pending: "bg-amber-100 text-amber-700",
  "n/a": "bg-slate-100 text-slate-500",
};

function AdsListPanel({ ads, reload }) {
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sortBy, setSortBy] = useState("newest");

  const setStatus = async (id, changes) => { await api.put(`/ads/${id}`, changes); reload(); };
  const remove = async (id) => { await api.delete(`/ads/${id}`); toast.success("Ad deleted"); reload(); };
  const togglePayment = async (ad) => {
    const next = ad.payment_status === "paid" ? "pending" : "paid";
    await api.put(`/ads/${ad.id}`, { payment_status: next });
    toast.success(`Marked ${next}`);
    reload();
  };

  let rows = ads.filter((ad) => {
    const q = query.trim().toLowerCase();
    const matchQ = !q || (ad.title || "").toLowerCase().includes(q) || (ad.sponsor_name || "").toLowerCase().includes(q) || (ad.owner_name || "").toLowerCase().includes(q) || (ad.owner_email || "").toLowerCase().includes(q);
    let matchS = true;
    if (statusFilter === "pending") matchS = ad.status !== "approved";
    else if (statusFilter === "approved") matchS = ad.status === "approved";
    else if (statusFilter === "active") matchS = ad.active === true;
    else if (statusFilter === "inactive") matchS = !ad.active;
    return matchQ && matchS;
  });
  rows = [...rows].sort((a, b) => {
    if (sortBy === "impressions") return (b.impressions || 0) - (a.impressions || 0);
    if (sortBy === "clicks") return (b.clicks || 0) - (a.clicks || 0);
    if (sortBy === "ending") return (a.days_remaining ?? 1e9) - (b.days_remaining ?? 1e9);
    return new Date(b.created_at || 0) - new Date(a.created_at || 0);
  });

  const STATUS_OPTS = [
    { k: "all", l: "All" }, { k: "pending", l: "Pending" }, { k: "approved", l: "Approved" },
    { k: "active", l: "Active" }, { k: "inactive", l: "Inactive" },
  ];

  return (
    <div className="space-y-5" data-testid="admin-ads-list">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="font-display text-2xl font-bold">All Advertisements <span className="text-muted-page font-normal text-lg">({ads.length})</span></h2>
      </div>

      <div className="surface border rounded-2xl p-4 flex flex-col lg:flex-row lg:items-center gap-3" data-testid="admin-ads-controls">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-page" />
          <input data-testid="admin-ads-search" placeholder="Search title, sponsor, owner or email…" value={query} onChange={(e) => setQuery(e.target.value)} className="w-full surface border rounded-lg pl-9 pr-3 py-2.5" />
        </div>
        <div className="flex flex-wrap gap-1.5" data-testid="admin-ads-status-filter">
          {STATUS_OPTS.map((o) => (
            <button key={o.k} onClick={() => setStatusFilter(o.k)} className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${statusFilter === o.k ? "text-white" : "surface"}`} style={statusFilter === o.k ? { background: "var(--brand)" } : {}}>{o.l}</button>
          ))}
        </div>
        <select data-testid="admin-ads-sort" value={sortBy} onChange={(e) => setSortBy(e.target.value)} className="surface border rounded-lg px-3 py-2.5 text-sm">
          <option value="newest">Newest</option>
          <option value="impressions">Most impressions</option>
          <option value="clicks">Most clicks</option>
          <option value="ending">Ending soon</option>
        </select>
      </div>

      {rows.length === 0 && <p className="text-muted-page">No ads match your filters.</p>}

      <div className="space-y-3">
        {rows.map((ad) => (
          <div key={ad.id} className="surface border rounded-xl p-4" data-testid={`admin-ad-${ad.id}`}>
            <div className="flex items-start gap-4 flex-wrap lg:flex-nowrap">
              {ad.image_url ? <img src={mediaUrl(ad.image_url)} alt="" className="w-16 h-16 object-cover rounded-lg shrink-0" /> : <div className="w-16 h-16 rounded-lg grid place-items-center shrink-0" style={{ background: "var(--honey-100)" }}><Megaphone className="w-5 h-5 brand-text" /></div>}

              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-display text-lg font-bold truncate">{ad.title}</span>
                  <span className={`text-[10px] font-tech uppercase px-2 py-0.5 rounded-full ${ad.status === "approved" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>{ad.status}</span>
                  <span className={`text-[10px] font-tech uppercase px-2 py-0.5 rounded-full ${ad.active ? "bg-sky-100 text-sky-700" : "bg-slate-100 text-slate-500"}`}>{ad.active ? "active" : "inactive"}</span>
                  <span className="text-[10px] font-tech px-2 py-0.5 rounded-full" style={{ background: "var(--honey-100)", color: "var(--honey-900)" }}>{ad.position}</span>
                </div>

                <p className="text-xs text-muted-page mt-1">{ad.sponsor_name} {ad.price ? `· $${ad.price} (${ad.package_name})` : ""}</p>

                {/* Buyer / owner contact */}
                <p className="text-xs text-muted-page mt-1" data-testid={`admin-ad-owner-${ad.id}`}>
                  <span className="font-semibold">Buyer:</span> {ad.owner_name || "—"}
                  {ad.owner_email && <> · <a href={`mailto:${ad.owner_email}`} className="brand-text hover:underline">{ad.owner_email}</a></>}
                </p>

                {ad.payer_note && <p className="text-xs text-muted-page mt-1 italic">"{ad.payer_note}"</p>}

                {/* Stats row */}
                <div className="flex flex-wrap gap-x-5 gap-y-1 mt-2 text-xs">
                  <span className="inline-flex items-center gap-1" data-testid={`admin-ad-impressions-${ad.id}`}><Eye className="w-3.5 h-3.5 text-muted-page" /> {ad.impressions ?? 0} views</span>
                  <span className="inline-flex items-center gap-1" data-testid={`admin-ad-clicks-${ad.id}`}><MousePointerClick className="w-3.5 h-3.5 text-muted-page" /> {ad.clicks ?? 0} clicks</span>
                  <span className="text-muted-page">CTR {(ad.impressions ? ((ad.clicks || 0) / ad.impressions * 100) : 0).toFixed(1)}%</span>
                  <span className="text-muted-page" data-testid={`admin-ad-dates-${ad.id}`}>{fmtDate(ad.start_at)} → {fmtDate(ad.end_at)}</span>
                  {ad.days_remaining != null && <span className={`font-semibold ${ad.days_remaining <= 3 ? "text-rose-600" : "text-muted-page"}`}>{ad.days_remaining}d left</span>}
                </div>

                {/* Actions */}
                <div className="flex flex-wrap gap-2 mt-3">
                  {ad.status !== "approved" && <button data-testid={`approve-ad-${ad.id}`} onClick={() => setStatus(ad.id, { status: "approved", active: true })} className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-full bg-emerald-600 text-white"><Check className="w-3 h-3" /> Approve</button>}
                  <button onClick={() => setStatus(ad.id, { active: !ad.active })} className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-full surface border">{ad.active ? "Deactivate" : "Activate"}</button>
                  <button data-testid={`payment-ad-${ad.id}`} onClick={() => togglePayment(ad)} className={`inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-full ${PAY_STYLES[ad.payment_status] || PAY_STYLES["n/a"]}`}><CreditCard className="w-3 h-3" /> {ad.payment_status === "paid" ? "Paid" : ad.payment_status === "pending" ? "Mark paid" : "Payment n/a"}</button>
                  <button data-testid={`delete-ad-${ad.id}`} onClick={() => remove(ad.id)} className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-full bg-rose-50 text-rose-600 border border-rose-200"><Trash2 className="w-3 h-3" /> Delete</button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ThemePanel({ settings, reload }) {
  const { upload, uploading } = useUpload();
  const { setSettings, loadSettings } = useSettings();
  const [form, setForm] = useState(settings);
  const sb = form.sponsor_box || {};

  const buildPayload = () => ({
    site_name: form.site_name, hero_title: form.hero_title, hero_subtitle: form.hero_subtitle,
    hero_image: form.hero_image, hero_blurb: form.hero_blurb, logo_insulator_image: form.logo_insulator_image,
    font_display: form.font_display, font_body: form.font_body,
    copyright_holder: form.copyright_holder,
    theme_mode: form.theme_mode, background_image: form.background_image || "",
    max_ads_home: parseInt(form.max_ads_home), max_ads_catalog: parseInt(form.max_ads_catalog),
    paypal_link: form.paypal_link, admin_secret_word: form.admin_secret_word, sponsor_box: form.sponsor_box,
    ad_packages: form.ad_packages, external_trade_surcharge: parseFloat(form.external_trade_surcharge),
  });

  const preview = () => {
    setSettings((prev) => ({ ...(prev || {}), ...buildPayload() }));
    toast.message("Previewing changes — not saved yet", { description: "Click Save to keep them, or Reset preview to discard." });
  };

  const resetPreview = async () => { await loadSettings(); toast.success("Preview reset"); };

  const save = async () => {
    await api.put("/settings", buildPayload());
    toast.success("Settings saved");
    reload();
  };

  return (
    <div className="grid lg:grid-cols-2 gap-6" data-testid="admin-theme-panel">
      <div className="surface border rounded-2xl p-6 space-y-4">
        <h2 className="font-display text-2xl font-bold">Appearance</h2>
        <div>
          <label className="block text-xs font-tech uppercase tracking-widest text-muted-page mb-1">Site / banner name</label>
          <input data-testid="site-name-input" value={form.site_name || ""} onChange={(e) => setForm((f) => ({ ...f, site_name: e.target.value }))} className="w-full surface border rounded-lg px-3 py-2.5" />
        </div>
        <div>
          <label className="block text-xs font-tech uppercase tracking-widest text-muted-page mb-1">Homepage banner heading</label>
          <input data-testid="hero-title-input" value={form.hero_title || ""} onChange={(e) => setForm((f) => ({ ...f, hero_title: e.target.value }))} className="w-full surface border rounded-lg px-3 py-2.5" />
        </div>
        <div>
          <label className="block text-xs font-tech uppercase tracking-widest text-muted-page mb-1">Homepage banner subtitle</label>
          <textarea data-testid="hero-subtitle-input" value={form.hero_subtitle || ""} onChange={(e) => setForm((f) => ({ ...f, hero_subtitle: e.target.value }))} rows={2} className="w-full surface border rounded-lg px-3 py-2.5" />
        </div>
        <div>
          <label className="block text-xs font-tech uppercase tracking-widest text-muted-page mb-1">Homepage blurb / tagline</label>
          <input data-testid="hero-blurb-input" value={form.hero_blurb || ""} onChange={(e) => setForm((f) => ({ ...f, hero_blurb: e.target.value }))} className="w-full surface border rounded-lg px-3 py-2.5" />
        </div>
        <div>
          <label className="block text-xs font-tech uppercase tracking-widest text-muted-page mb-1">Morph insulator image (honey-drop → insulator)</label>
          <div className="flex items-center gap-3">
            <label className="cursor-pointer inline-flex items-center gap-2 px-4 py-2 rounded-full surface border text-sm font-semibold">
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImagePlus className="w-4 h-4" />} Upload
              <input type="file" accept="image/*" className="hidden" data-testid="morph-image-input" onChange={async (e) => { const f = e.target.files?.[0]; if (f) { const url = await upload(f); setForm((s) => ({ ...s, logo_insulator_image: url })); } }} />
            </label>
            {form.logo_insulator_image && <img src={mediaUrl(form.logo_insulator_image)} alt="" className="w-14 h-14 object-contain rounded-lg" style={{ background: "var(--honey-100)" }} />}
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-tech uppercase tracking-widest text-muted-page mb-1">Heading font</label>
            <select data-testid="font-display-select" value={form.font_display || "Cormorant Garamond"} onChange={(e) => setForm((f) => ({ ...f, font_display: e.target.value }))} className="w-full surface border rounded-lg px-3 py-2.5">
              {DISPLAY_FONTS.map((ff) => <option key={ff} value={ff}>{ff}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-tech uppercase tracking-widest text-muted-page mb-1">Body font</label>
            <select data-testid="font-body-select" value={form.font_body || "Manrope"} onChange={(e) => setForm((f) => ({ ...f, font_body: e.target.value }))} className="w-full surface border rounded-lg px-3 py-2.5">
              {BODY_FONTS.map((ff) => <option key={ff} value={ff}>{ff}</option>)}
            </select>
          </div>
        </div>
        <div>
          <label className="block text-xs font-tech uppercase tracking-widest text-muted-page mb-1">Copyright / owner name (footer & legal)</label>
          <input data-testid="copyright-holder-input" value={form.copyright_holder || ""} onChange={(e) => setForm((f) => ({ ...f, copyright_holder: e.target.value }))} className="w-full surface border rounded-lg px-3 py-2.5" />
        </div>
        <div>
          <label className="block text-xs font-tech uppercase tracking-widest text-muted-page mb-2">Color theme</label>
          <div className="grid grid-cols-2 gap-2" data-testid="admin-theme-switcher">
            {THEME_MODES.map((m) => (
              <button key={m.key} onClick={() => setForm((f) => ({ ...f, theme_mode: m.key }))} className={`px-3 py-2.5 rounded-lg text-sm font-semibold border ${form.theme_mode === m.key ? "text-white" : "surface"}`} style={form.theme_mode === m.key ? { background: "var(--brand)" } : {}}>{m.label}</button>
            ))}
          </div>
        </div>
        <div>
          <label className="block text-xs font-tech uppercase tracking-widest text-muted-page mb-1">Homepage hero image</label>
          <div className="flex items-center gap-3">
            <label className="cursor-pointer inline-flex items-center gap-2 px-4 py-2 rounded-full surface border text-sm font-semibold">
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImagePlus className="w-4 h-4" />} Upload hero
              <input type="file" accept="image/*" className="hidden" data-testid="hero-image-input" onChange={async (e) => { const f = e.target.files?.[0]; if (f) { const url = await upload(f); setForm((s) => ({ ...s, hero_image: url })); } }} />
            </label>
          </div>
          {form.hero_image && <img src={mediaUrl(form.hero_image)} alt="" className="mt-2 w-full h-24 object-cover rounded-lg" />}
        </div>
        <div>
          <label className="block text-xs font-tech uppercase tracking-widest text-muted-page mb-1">Site background photo (whole app)</label>
          <div className="flex items-center gap-3">
            <label className="cursor-pointer inline-flex items-center gap-2 px-4 py-2 rounded-full surface border text-sm font-semibold">
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImagePlus className="w-4 h-4" />} Upload
              <input type="file" accept="image/*" className="hidden" onChange={async (e) => { const f = e.target.files?.[0]; if (f) { const url = await upload(f); setForm((s) => ({ ...s, background_image: url })); } }} />
            </label>
            {form.background_image && <button onClick={() => setForm((f) => ({ ...f, background_image: "" }))} className="text-xs text-rose-600">Remove</button>}
          </div>
          {form.background_image && <img src={mediaUrl(form.background_image)} alt="" className="mt-2 w-full h-24 object-cover rounded-lg" />}
        </div>
      </div>

      <div className="surface border rounded-2xl p-6 space-y-4">
        <h2 className="font-display text-2xl font-bold">Ad controls</h2>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-tech uppercase tracking-widest text-muted-page mb-1">Max ads · homepage</label>
            <input data-testid="max-ads-home-input" type="number" min={0} value={form.max_ads_home} onChange={(e) => setForm((f) => ({ ...f, max_ads_home: e.target.value }))} className="w-full surface border rounded-lg px-3 py-2.5" />
          </div>
          <div>
            <label className="block text-xs font-tech uppercase tracking-widest text-muted-page mb-1">Max ads · catalog</label>
            <input data-testid="max-ads-catalog-input" type="number" min={0} value={form.max_ads_catalog} onChange={(e) => setForm((f) => ({ ...f, max_ads_catalog: e.target.value }))} className="w-full surface border rounded-lg px-3 py-2.5" />
          </div>
        </div>
        <div>
          <label className="block text-xs font-tech uppercase tracking-widest text-muted-page mb-1">PayPal.me link</label>
          <input data-testid="paypal-link-input" value={form.paypal_link || ""} onChange={(e) => setForm((f) => ({ ...f, paypal_link: e.target.value }))} className="w-full surface border rounded-lg px-3 py-2.5" />
        </div>
        <div>
          <label className="block text-xs font-tech uppercase tracking-widest text-muted-page mb-1">External trade surcharge ($)</label>
          <input data-testid="surcharge-input" type="number" min={0} step="0.01" value={form.external_trade_surcharge ?? 0.25} onChange={(e) => setForm((f) => ({ ...f, external_trade_surcharge: e.target.value }))} className="w-full surface border rounded-lg px-3 py-2.5" />
          <p className="text-xs text-muted-page mt-1">Charged (via PayPal) on trade offers marked as external.</p>
        </div>
        <div>
          <label className="flex items-center gap-2 text-xs font-tech uppercase tracking-widest text-muted-page mb-1"><KeyRound className="w-3.5 h-3.5" /> Admin secret word</label>
          <input data-testid="admin-secret-input" value={form.admin_secret_word || ""} onChange={(e) => setForm((f) => ({ ...f, admin_secret_word: e.target.value }))} className="w-full surface border rounded-lg px-3 py-2.5" />
          <p className="text-xs text-muted-page mt-1">Users who enter this word on their Profile become administrators.</p>
        </div>

        <div className="border-t pt-4" style={{ borderColor: "var(--page-border)" }}>
          <div className="flex items-center justify-between mb-2">
            <span className="font-display text-lg font-bold">Sponsor box</span>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!sb.enabled} onChange={(e) => setForm((f) => ({ ...f, sponsor_box: { ...sb, enabled: e.target.checked } }))} /> Enabled</label>
          </div>
          <input placeholder="Sponsor box title" value={sb.title || ""} onChange={(e) => setForm((f) => ({ ...f, sponsor_box: { ...sb, title: e.target.value } }))} className="w-full surface border rounded-lg px-3 py-2.5 mb-2" />
          <input placeholder="Text" value={sb.text || ""} onChange={(e) => setForm((f) => ({ ...f, sponsor_box: { ...sb, text: e.target.value } }))} className="w-full surface border rounded-lg px-3 py-2.5 mb-2" />
          <input placeholder="Target URL" value={sb.target_url || ""} onChange={(e) => setForm((f) => ({ ...f, sponsor_box: { ...sb, target_url: e.target.value } }))} className="w-full surface border rounded-lg px-3 py-2.5 mb-2" />
          <label className="cursor-pointer inline-flex items-center gap-2 px-4 py-2 rounded-full surface border text-sm font-semibold">
            <ImagePlus className="w-4 h-4" /> Sponsor image
            <input type="file" accept="image/*" className="hidden" onChange={async (e) => { const f = e.target.files?.[0]; if (f) { const url = mediaUrl(await upload(f)); setForm((s) => ({ ...s, sponsor_box: { ...s.sponsor_box, image_url: url } })); } }} />
          </label>
        </div>

        <div className="border-t pt-4" style={{ borderColor: "var(--page-border)" }} data-testid="ad-packages-editor">
          <span className="font-display text-lg font-bold">Ad packages & prices</span>
          <p className="text-xs text-muted-page mb-2">Set the name, price and duration buyers see on the Advertise page.</p>
          <div className="space-y-2">
            {Object.entries(form.ad_packages || {}).map(([key, p]) => (
              <div key={key} className="grid grid-cols-[1fr_70px_60px] gap-2 items-center" data-testid={`package-row-${key}`}>
                <input value={p.name} onChange={(e) => setForm((f) => ({ ...f, ad_packages: { ...f.ad_packages, [key]: { ...p, name: e.target.value } } }))} className="surface border rounded-lg px-2 py-1.5 text-sm" placeholder="Name" />
                <input type="number" min={0} value={p.price} data-testid={`package-price-${key}`} onChange={(e) => setForm((f) => ({ ...f, ad_packages: { ...f.ad_packages, [key]: { ...p, price: parseFloat(e.target.value) || 0 } } }))} className="surface border rounded-lg px-2 py-1.5 text-sm" placeholder="$" />
                <input type="number" min={1} value={p.days} onChange={(e) => setForm((f) => ({ ...f, ad_packages: { ...f.ad_packages, [key]: { ...p, days: parseInt(e.target.value) || 1 } } }))} className="surface border rounded-lg px-2 py-1.5 text-sm" placeholder="days" />
              </div>
            ))}
          </div>
          <p className="text-[10px] font-tech uppercase tracking-widest text-muted-page mt-1">name · price ($) · duration (days)</p>
        </div>

        <div className="flex flex-wrap gap-2">
          <button type="button" data-testid="preview-settings-btn" onClick={preview} className="flex-1 min-w-[120px] py-3 rounded-full font-semibold surface border">Preview</button>
          <button type="button" data-testid="reset-preview-btn" onClick={resetPreview} className="py-3 px-4 rounded-full font-semibold surface border">Reset preview</button>
          <ConfirmButton
            testId="save-settings-btn"
            onConfirm={save}
            title="Save site settings?"
            description="These changes will go live for all visitors immediately. Are you sure you want to save?"
            confirmLabel="Yes, save changes"
            className="flex-1 min-w-[140px] py-3 rounded-full font-semibold text-white"
            style={{ background: "var(--brand)" }}
          >
            Save all settings
          </ConfirmButton>
        </div>
      </div>
    </div>
  );
}

function ModerationPanel({ pendingIns, contributions, reload }) {
  const setInsStatus = async (id, status) => {
    await api.put(`/admin/insulators/${id}/status`, { status });
    toast.success(`Insulator ${status}`);
    reload();
  };
  const contribAction = async (id, action) => {
    await api.post(`/admin/contributions/${id}/${action}`);
    toast.success(`Addition ${action === "approve" ? "approved" : "rejected"}`);
    reload();
  };

  return (
    <div className="space-y-8" data-testid="admin-moderation-panel">
      <div>
        <h2 className="font-display text-2xl font-bold mb-3">Pending insulator submissions ({pendingIns.length})</h2>
        {pendingIns.length === 0 ? <p className="text-muted-page">Nothing awaiting review.</p> : (
          <div className="grid gap-3 sm:grid-cols-2">
            {pendingIns.map((ins) => (
              <div key={ins.id} className="surface border rounded-xl p-4 flex gap-3" data-testid={`pending-ins-${ins.id}`}>
                {ins.image_url ? <img src={mediaUrl(ins.image_url)} alt="" className="w-16 h-16 object-contain rounded-lg" style={{ background: "var(--honey-100)" }} /> : <div className="w-16 h-16 rounded-lg" style={{ background: "var(--honey-100)" }} />}
                <div className="flex-1 min-w-0">
                  <p className="font-display text-lg font-bold">{ins.cd_number} · {ins.name}</p>
                  <p className="text-xs text-muted-page">{ins.maker} · {ins.color} · by {ins.created_by_name}</p>
                  <div className="flex gap-2 mt-2">
                    <button data-testid={`approve-ins-${ins.id}`} onClick={() => setInsStatus(ins.id, "approved")} className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-full bg-emerald-600 text-white"><Check className="w-3 h-3" /> Approve</button>
                    <button data-testid={`reject-ins-${ins.id}`} onClick={() => setInsStatus(ins.id, "rejected")} className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-full bg-rose-50 text-rose-600 border border-rose-200"><X className="w-3 h-3" /> Reject</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div>
        <h2 className="font-display text-2xl font-bold mb-3">Pending additions to existing insulators ({contributions.length})</h2>
        {contributions.length === 0 ? <p className="text-muted-page">No submitted additions.</p> : (
          <div className="space-y-3">
            {contributions.map((c) => (
              <div key={c.id} className="surface border rounded-xl p-4" data-testid={`contribution-${c.id}`}>
                <p className="font-semibold">Addition to <span className="brand-text">{c.insulator_name}</span> <span className="text-xs text-muted-page">by {c.user_name}</span></p>
                {c.images?.length > 0 && (
                  <div className="flex gap-3 flex-wrap mt-2">
                    {c.images.map((im) => (
                      <div key={im} className="w-40">
                        <img src={mediaUrl(im)} alt="" className="w-40 h-40 object-cover rounded-lg border" style={{ borderColor: "var(--page-border)" }} />
                        {c.image_captions?.[im] && <p className="text-xs text-muted-page mt-1 leading-snug">{c.image_captions[im]}</p>}
                      </div>
                    ))}
                  </div>
                )}
                {c.fields && Object.keys(c.fields).length > 0 && (
                  <div className="text-sm mt-2 space-y-0.5">
                    {Object.entries(c.fields).map(([k, v]) => <p key={k}><span className="font-tech text-xs uppercase text-muted-page">{k}:</span> {String(v)}</p>)}
                  </div>
                )}
                {c.note && <p className="text-sm text-muted-page mt-1 italic">Note: {c.note}</p>}
                <div className="flex gap-2 mt-2">
                  <button data-testid={`approve-contrib-${c.id}`} onClick={() => contribAction(c.id, "approve")} className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-full bg-emerald-600 text-white"><Check className="w-3 h-3" /> Approve & merge</button>
                  <button data-testid={`reject-contrib-${c.id}`} onClick={() => contribAction(c.id, "reject")} className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-full bg-rose-50 text-rose-600 border border-rose-200"><X className="w-3 h-3" /> Reject</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function AdminsPanel({ admins, isOwner, reload }) {  const [email, setEmail] = useState("");

  const grant = async () => {
    try {
      await api.post("/admin/admins/grant", { email });
      toast.success("Admin granted");
      setEmail("");
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not grant");
    }
  };
  const revoke = async (id) => {
    try { await api.post("/admin/admins/revoke", { user_id: id }); toast.success("Access revoked"); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not revoke"); }
  };

  return (
    <div className="max-w-2xl" data-testid="admin-admins-panel">
      {isOwner && (
        <div className="surface border rounded-2xl p-6 mb-6">
          <h2 className="font-display text-2xl font-bold mb-1">Add an administrator</h2>
          <p className="text-sm text-muted-page mb-3">Grant admin rights to a registered collector by their email.</p>
          <div className="flex gap-2">
            <input data-testid="grant-admin-email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="collector@email.com" className="flex-1 surface border rounded-lg px-3 py-2.5" />
            <button data-testid="grant-admin-btn" onClick={grant} className="px-5 rounded-lg font-semibold text-white" style={{ background: "var(--brand)" }}>Grant</button>
          </div>
        </div>
      )}
      <div className="surface border rounded-2xl overflow-hidden">
        {admins.map((a) => (
          <div key={a.id} className="flex items-center justify-between px-5 py-3 border-b" style={{ borderColor: "var(--page-border)" }} data-testid={`admin-row-${a.id}`}>
            <div className="flex items-center gap-3">
              <span className="w-9 h-9 rounded-full grid place-items-center text-xs font-bold" style={{ background: "var(--honey-500)", color: "#3a2a10" }}>{(a.username || "?")[0].toUpperCase()}</span>
              <div>
                <p className="font-semibold">{a.username} <span className={`text-[10px] font-tech uppercase px-2 py-0.5 rounded-full ml-1 ${a.role === "owner" ? "bg-amber-200 text-amber-900" : "bg-slate-100 text-slate-700"}`}>{a.role}</span></p>
                <p className="text-xs text-muted-page">{a.email}</p>
              </div>
            </div>
            {isOwner && a.role !== "owner" && <button data-testid={`revoke-admin-${a.id}`} onClick={() => revoke(a.id)} className="text-xs px-3 py-1.5 rounded-full bg-rose-50 text-rose-600 border border-rose-200 inline-flex items-center gap-1"><X className="w-3 h-3" /> Revoke</button>}
          </div>
        ))}
      </div>
    </div>
  );
}


function MembersPanel({ isOwner, currentUserId }) {
  const [q, setQ] = useState("");
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const reqRef = useRef(0);

  const load = useCallback(async (term) => {
    const seq = ++reqRef.current;
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/users${term ? `?q=${encodeURIComponent(term)}` : ""}`);
      if (seq === reqRef.current) setUsers(data); // ignore stale responses
    } finally {
      if (seq === reqRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => load(q.trim()), q ? 300 : 0);
    return () => clearTimeout(t);
  }, [q, load]);

  const remove = async (u) => {
    try {
      const { data } = await api.delete(`/admin/users/${u.id}`);
      toast.success(`Deleted ${data.username || "account"}`);
      setUsers((list) => list.filter((x) => x.id !== u.id));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not delete account");
    }
  };

  const roleBadge = (role) => role === "owner"
    ? "bg-amber-200 text-amber-900"
    : role === "admin" ? "bg-indigo-100 text-indigo-700" : "bg-slate-100 text-slate-700";

  return (
    <div>
      <div className="flex items-center gap-2 mb-1"><UserCog className="w-5 h-5 brand-text" /><h2 className="font-display text-2xl font-bold">Members</h2></div>
      <p className="text-sm text-muted-page mb-4">Remove accounts that belittle or harass others. Forum posts are kept but shown as <strong>[deleted user]</strong>; cataloged insulators stay. Personal data (collection, messages, trades, wishlist, ads) is removed. Only the Owner can delete an administrator.</p>

      <div className="relative mb-4 max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-page" />
        <input data-testid="member-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name, username or email…" className="w-full surface border rounded-full pl-9 pr-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-amber-400" />
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-muted-page"><Loader2 className="w-4 h-4 animate-spin" /> Loading members…</div>
      ) : users.length === 0 ? (
        <p className="text-muted-page">No members found.</p>
      ) : (
        <div className="space-y-2" data-testid="members-list">
          {users.map((u) => {
            const isSelf = u.id === currentUserId;
            const canDelete = u.role !== "owner" && !isSelf && (u.role !== "admin" || isOwner);
            return (
              <div key={u.id} data-testid={`member-row-${u.id}`} className="surface border rounded-xl px-4 py-3 flex items-center gap-3">
                <div className="w-9 h-9 rounded-full grid place-items-center shrink-0 font-display font-bold text-white" style={{ background: "var(--honey-500)" }}>
                  {(u.username || u.name || "?").charAt(0).toUpperCase()}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="font-semibold truncate">
                    {u.username || u.name}
                    <span className={`text-[10px] font-tech uppercase px-2 py-0.5 rounded-full ml-2 ${roleBadge(u.role)}`}>{u.role}</span>
                    {isSelf && <span className="text-[10px] font-tech uppercase px-2 py-0.5 rounded-full ml-1 bg-slate-100 text-slate-700">you</span>}
                  </p>
                  <p className="text-xs text-muted-page truncate">{u.email} · {u.collection_count} pieces · {u.forum_count} forum posts</p>
                </div>
                {canDelete ? (
                  <ConfirmButton
                    testId={`delete-member-${u.id}`}
                    onConfirm={() => remove(u)}
                    title={`Delete ${u.username || "this account"}?`}
                    description="This permanently deletes the account and their personal data (collection, messages, trades, wishlist, ads). Their forum posts stay but show as [deleted user]. This cannot be undone."
                    confirmLabel="Yes, delete account"
                    className="shrink-0 text-xs px-3 py-1.5 rounded-full bg-rose-50 text-rose-600 border border-rose-200 inline-flex items-center gap-1 hover:bg-rose-100 transition-colors"
                  >
                    <Trash2 className="w-3 h-3" /> Delete
                  </ConfirmButton>
                ) : (
                  <span className="shrink-0 text-xs text-muted-page px-2">{u.role === "owner" ? "Protected" : isSelf ? "—" : "Owner only"}</span>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
